// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type ChangeEvent } from 'react';
import { styled } from '@compiled/react';
import { Field, ErrorMessage, FormHeader, RequiredAsterisk } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import UserPicker from '@atlassian/jira-user-picker/src/index.tsx';
import type { User as SelectedUser } from '@atlassian/jira-user-picker/src/model/index.tsx';
import type { DefaultAssignee as DefaultProjectAssignee } from '../../../state/project/types.tsx';
import type { DefaultAssignee, User, EditableComponent } from '../../model/types.tsx';
import AssigneeSelect from '../assignee-select/view.tsx';
import messages from './messages.tsx';

type Props = {
	intl: IntlShape;
	onUpdateComponent: (component: EditableComponent) => void;
	baseUrl: string;
	name: string;
	description: string | undefined;
	lead: User | undefined;
	defaultAssignee: DefaultAssignee;
	projectDefaultAssignee: DefaultProjectAssignee | undefined;
	projectLeadName: string | undefined;
	nameErrorMessage: string | undefined;
};

type FieldTextRef = {
	focus: () => void;
};

// eslint-disable-next-line jira/react/no-class-components
class ModalCommonContent extends PureComponent<Props> {
	componentDidUpdate(prevProps: Props) {
		if (this.props.nameErrorMessage && !prevProps.nameErrorMessage) {
			// do it a little bit later to avoid animation clipping
			setTimeout(() => {
				if (this.nameFieldInstance) this.nameFieldInstance.focus();
			});
		}
	}

	onChangeComponentName = (e: ChangeEvent<HTMLInputElement>) => {
		this.inputChange({
			name: e.target.value,
		});
	};

	onChangeComponentDescription = (e: ChangeEvent<HTMLInputElement>) => {
		this.inputChange({
			description: e.target.value,
		});
	};

	onLeadSelected = (user: SelectedUser) => {
		this.inputChange({
			lead: user
				? {
						accountId: user.accountId,
						displayName: user.displayName,
						avatarUrl: user.avatarUrl,
						/*
                      This is hardcoded to true as user from the user picker does not return its status.
                      Since user picker is always returning the active user, it should be safe to hard code to be active user.
                       */
						active: true,
					}
				: undefined,
		});
	};

	onAssigneeChange = (assignee: DefaultAssignee) => {
		this.inputChange({
			defaultAssignee: assignee,
		});
	};

	nameFieldInstance: undefined | FieldTextRef;

	inputChange(update: Partial<EditableComponent>) {
		const { name, description, lead, defaultAssignee, onUpdateComponent } = this.props;
		const oldComponent = {
			name,
			description,
			lead,
			defaultAssignee,
		};

		const newComponent = { ...oldComponent, ...update };
		onUpdateComponent(newComponent);
	}

	generateLeadWithStatus(lead: User | undefined) {
		const { formatMessage } = this.props.intl;

		if (lead && lead.active === false) {
			return {
				...lead,
				displayName: formatMessage(messages.deactivated, {
					userName: lead.displayName,
				}),
			};
		}
		return lead;
	}

	render() {
		const {
			intl: { formatMessage },
			nameErrorMessage,
			baseUrl,
			projectLeadName,
			projectDefaultAssignee,
			name,
			description,
			lead,
			defaultAssignee,
		} = this.props;

		const isInvalid = !!nameErrorMessage;
		const selectedLead = this.generateLeadWithStatus(lead);

		return (
			<div data-test-id="project-directories.components.common.components.configuration">
				<FormHeader>
					<p aria-hidden="true">
						{formatMessage(messages.requiredFieldsInformMessage)} <RequiredAsterisk />
					</p>
				</FormHeader>
				<Field
					name="component-name"
					isRequired
					label={<FieldLabelWrapper>{formatMessage(messages.componentName)}</FieldLabelWrapper>}
				>
					{({ fieldProps }) => (
						<>
							<Textfield
								{...fieldProps}
								autoFocus
								onChange={this.onChangeComponentName}
								value={name}
								isInvalid={isInvalid}
								aria-invalid={isInvalid}
								maxLength={255}
								ref={(instance) => {
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									this.nameFieldInstance = instance as FieldTextRef;
								}}
							/>
							{nameErrorMessage !== undefined && nameErrorMessage !== '' && (
								<ErrorMessage>{nameErrorMessage}</ErrorMessage>
							)}
						</>
					)}
				</Field>
				<Field
					name="component-description"
					label={<FieldLabelWrapper>{formatMessage(messages.description)}</FieldLabelWrapper>}
				>
					{({ fieldProps }) => (
						<Textfield
							{...fieldProps}
							onChange={this.onChangeComponentDescription}
							value={description}
						/>
					)}
				</Field>
				<UserPicker
					label={formatMessage(messages.componentLead)}
					placeholder={formatMessage(messages.componentLeadPlaceholder)}
					// @ts-expect-error - Type 'import("/opt/atlassian/pipelines/agent/build/jira/src/packages/project-pages/project-directories/components/src/common/model/types").User | undefined' is not assignable to type 'import("/opt/atlassian/pipelines/agent/build/jira/src/packages/platform/ui/pickers/user/src/model/index").User | undefined'.
					selectedUser={selectedLead}
					baseUrl={baseUrl}
					// @ts-expect-error - Type '(user: SelectedUser) => void' is not assignable to type '(arg1: User | undefined, args_1: UIAnalyticsEvent) => void'.
					onChange={this.onLeadSelected}
					shouldFitContainer
				/>
				<AssigneeSelect
					label={formatMessage(messages.defaultAssignee)}
					projectDefaultAssignee={projectDefaultAssignee}
					projectLeadName={projectLeadName}
					componentLeadName={lead && lead.displayName}
					onSelected={this.onAssigneeChange}
					value={defaultAssignee}
				/>
			</div>
		);
	}
}

export default injectIntl(ModalCommonContent);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldLabelWrapper = styled.div({
	display: 'inline-block',
	/* The AK Field component has a margin-top of 8px. However the label for the component lead UserPicker has padding-top */
	/* of 20px. We wrap our AK Field labels with an extra 12px of padding to keep spacing consistent. */
	marginTop: token('space.150', '12px'),
});
